import { type QueryResult } from '@graphql-box/core';
import { type QueryError } from '@graphql-box/helpers';

import { useMultiQuery } from '@graphql-box/react';
import { type Movie, ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';

const queries = {
  [ScreenType.Movie]: "\nquery GetMovieDetailsBrief(\n  $id: ID!\n) {\n  movie(id: $id) {\n    backdropPath\n    genres {\n      name\n    }\n    homepage\n    overview\n    popularity\n    posterPath\n    releaseDate\n    runtime\n    status\n    tagline\n    title\n    voteAverage\n    voteCount\n    ...MovieCertifications\n  }\n}\n\nfragment MovieCertifications on Movie {\n  releaseDates {\n    iso_3166_1\n    releaseDates {\n      certification\n    }\n  }\n}\n",
  [ScreenType.Tv]: "\nquery GetTvDetailsBrief(\n  $id: ID!\n) {\n  tv(id: $id) {\n    backdropPath\n    firstAirDate\n    genres {\n      name\n    }\n    homepage\n    inProduction\n    lastAirDate\n    lastEpisodeToAir {\n      airDate\n      episodeNumber\n      name\n      overview\n      seasonNumber\n    }\n    name\n    nextEpisodeToAir {\n      airDate\n      episodeNumber\n      name\n      overview\n      seasonNumber\n    }\n    numberOfSeasons\n    overview\n    popularity\n    posterPath\n    status\n    tagline\n    voteAverage\n    voteCount\n    ...TvCertifications\n  }\n}\n\nfragment TvCertifications on Tv {\n  contentRatings {\n    iso_3166_1\n    rating\n  }\n}\n",
};

export type UseMovieTvMultiQueryOptions = {
  initiator?: string;
  movieQuery?: string;
  tvQuery?: string;
};

export type UseMovieTvMultiQueryResult = {
  execute: (movieIds: string[] | undefined, tvIds: string[] | undefined) => void;
  movieLoading: boolean;
  movieResults: (
    | QueryError
    | QueryResult<{
        movie: Movie;
      }>
  )[];
  tvLoading: boolean;
  tvResults: (
    | QueryError
    | QueryResult<{
        tv: Tv;
      }>
  )[];
};

export const useMovieTvMultiQuery = ({
  initiator = 'useMovieTvMultiQuery',
  movieQuery,
  tvQuery,
}: UseMovieTvMultiQueryOptions = {}): UseMovieTvMultiQueryResult => {
  const {
    execute: movieExecute,
    loading: movieLoading,
    results: movieResults,
  } = useMultiQuery<{ movie: Movie }>(movieQuery ?? queries[ScreenType.Movie], {
    loading: true,
  });

  const {
    execute: tvExecute,
    loading: tvLoading,
    results: tvResults,
  } = useMultiQuery<{ tv: Tv }>(tvQuery ?? queries[ScreenType.Tv], {
    loading: true,
  });

  const execute = (movieIds: string[] | undefined, tvIds: string[] | undefined) => {
    if (movieIds) {
      const optionsSet = movieIds.map(id => ({ variables: { id } }));
      void movieExecute(optionsSet, { data: { initiator } });
    }

    if (tvIds) {
      const optionsSet = tvIds.map(id => ({ variables: { id } }));
      void tvExecute(optionsSet, { data: { initiator } });
    }
  };

  return {
    execute,
    movieLoading,
    movieResults: movieResults ?? [],
    tvLoading,
    tvResults: tvResults ?? [],
  };
};
